import { useEffect, useMemo, useState } from "react";
import { networkPillLabel } from "@/lib/networkLabels";
import {
  ArrowsLeftRight,
  CaretDown,
  CaretUp,
  MagnifyingGlass,
  ArrowSquareOut,
} from "@phosphor-icons/react";
import API from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FEED_PARTNER_OPTIONS } from "@/components/FeedPartnerSelect";

const NETWORKS = FEED_PARTNER_OPTIONS.filter((o) => o.value !== "all");

const DEFAULT_SOURCE = ["pubadhub", "pubadhub_kevin"];
const DEFAULT_PRIORITY = ["shopnomix_kevin", "shopnomix", "siteplug", "mrge"];

const NETWORK_COLORS = {
  impact: "bg-blue-50 text-blue-700",
  partnerize: "bg-indigo-50 text-indigo-700",
  skimlinks: "bg-orange-50 text-orange-700",
  mrge: "bg-emerald-50 text-emerald-700",
  cj: "bg-amber-50 text-amber-700",
  pubadhub: "bg-cyan-50 text-cyan-700",
  pubadhub_kevin: "bg-teal-50 text-teal-700",
  admedia: "bg-rose-50 text-rose-700",
  flexoffers: "bg-violet-50 text-violet-700",
  shopnomix_kevin: "bg-fuchsia-50 text-fuchsia-700",
  siteplug: "bg-sky-50 text-sky-700",
  shopnomix: "bg-lime-50 text-lime-700",
  pronomix: "bg-purple-50 text-purple-700",
  maxbounty: "bg-cyan-50 text-cyan-700",
  cashbackhive: "bg-teal-50 text-teal-700",
  fatcoupon: "bg-rose-50 text-rose-700",
  rga: "bg-violet-50 text-violet-700",
  giddyup: "bg-yellow-50 text-yellow-700",
  rga_vishy: "bg-violet-50 text-violet-700",
  rga_ashok: "bg-pink-50 text-pink-700",
};

function NetworkPill({ network }) {
  const n = (network || "").toLowerCase();
  if (!n) return <span className="text-slate-400 text-xs">—</span>;
  const cls = NETWORK_COLORS[n] || "bg-slate-100 text-slate-700";
  const lbl = networkPillLabel(n);
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${cls}`}>
      {lbl}
    </span>
  );
}

function StatusPill({ status }) {
  const s = (status || "").toLowerCase() || "—";
  const cls = {
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    paused: "bg-amber-50 text-amber-700 border-amber-200",
    inactive: "bg-rose-50 text-rose-700 border-rose-200",
    disabled: "bg-rose-50 text-rose-700 border-rose-200",
    removed: "bg-rose-50 text-rose-700 border-rose-200",
    deleted: "bg-rose-50 text-rose-700 border-rose-200",
    network_disabled: "bg-orange-50 text-orange-800 border-orange-200",
  }[s] || "bg-slate-50 text-slate-600 border-slate-200";
  const lbl = s === "network_disabled" ? "net-disabled" : s;
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${cls}`}
      title={s === "network_disabled" ? "Tracking blocked: network disabled for this publisher" : undefined}
    >
      {lbl}
    </span>
  );
}

function netLabel(value) {
  return NETWORKS.find((n) => n.value === value)?.label || value;
}

function toggleInList(list, value, on) {
  const v = String(value || "").toLowerCase();
  if (on) {
    if (list.includes(v)) return list;
    return [...list, v];
  }
  return list.filter((x) => x !== v);
}

function movePriority(list, index, dir) {
  const next = [...list];
  const j = index + dir;
  if (j < 0 || j >= next.length) return list;
  [next[index], next[j]] = [next[j], next[index]];
  return next;
}

/**
 * Routed Brands → Suggest routes: preview domain+geo matches, select rows, apply.
 */
export default function SuggestRoutesPanel({ onApplied }) {
  const [pubs, setPubs] = useState([]);
  const [pubId, setPubId] = useState("");
  const [pubFilter, setPubFilter] = useState("");
  const [sourceNets, setSourceNets] = useState(DEFAULT_SOURCE);
  const [priority, setPriority] = useState(DEFAULT_PRIORITY);
  const [clicksDays, setClicksDays] = useState("0"); // 0 = all-time
  const [clicksFilter, setClicksFilter] = useState("with"); // with | zero | any
  const [sourceStatus, setSourceStatus] = useState("all"); // all | active | paused | inactive
  const [onlyUnrouted, setOnlyUnrouted] = useState(true);
  const [routeableOnly, setRouteableOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resultFilter, setResultFilter] = useState("routeable"); // all | routeable | no_match
  const [resultSearch, setResultSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get("/admin/publishers");
        if (!cancelled) setPubs(Array.isArray(data) ? data : (data?.items || []));
      } catch {
        if (!cancelled) toast.error("Failed to load publishers");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredPubs = useMemo(() => {
    const q = pubFilter.trim().toLowerCase();
    if (!q) return pubs;
    return pubs.filter((p) => {
      const hay = `${p.email || ""} ${p.sub_id || ""} ${p.company_name || ""} ${p.id || p._id || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [pubs, pubFilter]);

  const availableForPriority = NETWORKS.filter((n) => !priority.includes(n.value));

  const visibleItems = useMemo(() => {
    let rows = items;
    if (resultFilter === "routeable") rows = rows.filter((r) => r.route_available);
    else if (resultFilter === "no_match") rows = rows.filter((r) => !r.route_available);

    const q = resultSearch.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => {
        const hay = [
          r.source_brand_name,
          r.source_brand_id,
          r.source_network,
          r.source_domain,
          r.source_country,
          r.source_status,
          r.route_brand_name,
          r.route_brand_id,
          r.route_network,
          r.tracking_url,
          ...(r.route_options || []).flatMap((o) => [o.network, o.brand_name, o.brand_id]),
        ].join(" ").toLowerCase();
        return hay.includes(q);
      });
    }

    // Selected rows first, keep relative order otherwise
    return [...rows].sort((a, b) => {
      const aSel = selected.has(a.source_brand_id) ? 0 : 1;
      const bSel = selected.has(b.source_brand_id) ? 0 : 1;
      return aSel - bSel;
    });
  }, [items, resultFilter, resultSearch, selected]);

  const routeableItems = useMemo(
    () => items.filter((r) => r.route_available && r.route_brand_id),
    [items],
  );

  const selectedRouteable = useMemo(() => {
    return routeableItems.filter((r) => selected.has(r.source_brand_id));
  }, [routeableItems, selected]);

  const allRouteableSelected =
    routeableItems.length > 0 && routeableItems.every((r) => selected.has(r.source_brand_id));

  const runSuggest = async () => {
    if (!pubId) {
      toast.error("Select a publisher");
      return;
    }
    if (!sourceNets.length) {
      toast.error("Select at least one source network");
      return;
    }
    if (!priority.length) {
      toast.error("Add at least one route-priority network");
      return;
    }
    setLoading(true);
    setSelected(new Set());
    setResultSearch("");
    try {
      const days = Number(clicksDays);
      const { data } = await API.post("/admin/traffic-remaps/suggest", {
        publisher_id: pubId,
        source_networks: sourceNets,
        priority_networks: priority,
        clicks_days: days > 0 ? days : null,
        only_unrouted: onlyUnrouted,
        clicks_filter: clicksFilter,
        source_brand_status: sourceStatus,
        only_with_clicks: clicksFilter === "with",
        routeable_only: false,
      });
      setSummary(data);
      setItems(data?.items || []);
      setResultFilter(routeableOnly ? "routeable" : "all");
      const routeable = (data?.items || []).filter((r) => r.route_available);
      toast.success(
        `Found ${data?.total || 0} brands · ${routeable.length} with domain+geo route`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Suggest failed");
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (brandId, on) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(brandId);
      else next.delete(brandId);
      return next;
    });
  };

  const selectAllRouteable = (on) => {
    if (on) setSelected(new Set(routeableItems.map((r) => r.source_brand_id)));
    else setSelected(new Set());
  };

  const applySelected = async () => {
    if (!selectedRouteable.length) {
      toast.error("Select at least one routeable row");
      return;
    }
    setApplying(true);
    try {
      const { data } = await API.post("/admin/traffic-remaps/apply-suggestions", {
        publisher_id: pubId,
        items: selectedRouteable.map((r) => ({
          from_brand_id: r.source_brand_id,
          to_brand_id: r.route_brand_id,
        })),
        ensure_assign: true,
        pause_source: false,
      });
      const ok = data?.ok || 0;
      const errors = data?.errors || 0;
      if (ok && !errors) toast.success(`Routed ${ok} brand${ok === 1 ? "" : "s"}`);
      else if (ok) toast.warning(`Routed ${ok}, failed ${errors}`);
      else toast.error("Routing failed");
      setConfirmOpen(false);
      setSelected(new Set());
      onApplied?.();
      // Drop applied rows from preview when only_unrouted
      if (onlyUnrouted && ok) {
        const done = new Set(selectedRouteable.map((r) => r.source_brand_id));
        setItems((prev) => prev.filter((r) => !done.has(r.source_brand_id)));
        setSummary((s) => (s ? {
          ...s,
          total: Math.max(0, (s.total || 0) - ok),
          routeable: Math.max(0, (s.routeable || 0) - ok),
        } : s));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div data-testid="suggest-routes-panel">
      <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 p-4 space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Publisher</label>
            <Input
              className="mt-1 h-8 text-xs"
              placeholder="Filter email / sub_id…"
              value={pubFilter}
              onChange={(e) => setPubFilter(e.target.value)}
            />
            <select
              className="mt-1 w-full h-9 text-sm border border-slate-200 rounded-md bg-white"
              value={pubId}
              onChange={(e) => setPubId(e.target.value)}
              data-testid="suggest-publisher"
            >
              <option value="">Select publisher…</option>
              {filteredPubs.map((p) => {
                const id = p.id || p._id || "";
                return (
                  <option key={id} value={id}>
                    {(p.email || id) + (p.sub_id ? ` · ${p.sub_id}` : "")}
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
              Click window
            </label>
            <select
              className="mt-1 w-full h-9 text-sm border border-slate-200 rounded-md bg-white"
              value={clicksDays}
              onChange={(e) => setClicksDays(e.target.value)}
              data-testid="suggest-clicks-days"
            >
              <option value="0">All-time clicks</option>
              <option value="2">Last 2 days</option>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                  Source brand status
                </label>
                <select
                  className="mt-1 w-full h-8 text-xs border border-slate-200 rounded-md bg-white"
                  value={sourceStatus}
                  onChange={(e) => setSourceStatus(e.target.value)}
                  data-testid="suggest-source-status"
                >
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="active_net_disabled">Active + net disabled</option>
                  <option value="paused">Paused</option>
                  <option value="inactive">Inactive</option>
                  <option value="network_disabled">Network disabled</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                  Clicks
                </label>
                <select
                  className="mt-1 w-full h-8 text-xs border border-slate-200 rounded-md bg-white"
                  value={clicksFilter}
                  onChange={(e) => setClicksFilter(e.target.value)}
                  data-testid="suggest-clicks-filter"
                >
                  <option value="with">Has clicks (valid + blocked)</option>
                  <option value="zero">Zero clicks</option>
                  <option value="any">Any (with or zero)</option>
                </select>
              </div>
            </div>
            <div className="mt-2 flex flex-col gap-1.5 text-xs text-slate-600">
              <label className="flex items-center gap-2">
                <Checkbox checked={onlyUnrouted} onCheckedChange={(v) => setOnlyUnrouted(!!v)} />
                Only brands not already routed
              </label>
              <label className="flex items-center gap-2">
                <Checkbox checked={routeableOnly} onCheckedChange={(v) => setRouteableOnly(!!v)} />
                Default results filter to routeable only
              </label>
              <p className="text-[11px] text-slate-400">
                Match: domain + geo · target must be active · pause source off.
                Status: Active = live; Active + net disabled = catalog active but network off for pub;
                Paused = pub/assignment pause; Inactive = catalog inactive; Network disabled = pub disabled_networks.
              </p>
            </div>
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
            Source network (where traffic is today)
          </label>
          <div className="mt-2 flex flex-wrap gap-2">
            {NETWORKS.map((n) => {
              const on = sourceNets.includes(n.value);
              return (
                <button
                  key={n.value}
                  type="button"
                  onClick={() => setSourceNets((prev) => toggleInList(prev, n.value, !on))}
                  className={`px-2 py-1 rounded text-[11px] font-medium border ${
                    on
                      ? "bg-sky-50 border-sky-300 text-sky-800"
                      : "bg-white border-slate-200 text-slate-500"
                  }`}
                >
                  {n.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
            Route network priority (first match wins)
          </label>
          <div className="mt-2 space-y-1">
            {priority.map((net, idx) => (
              <div
                key={net}
                className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-2 py-1.5"
              >
                <span className="text-[10px] font-bold text-slate-400 w-5">{idx + 1}.</span>
                <NetworkPill network={net} />
                <span className="text-sm text-slate-800 flex-1">{netLabel(net)}</span>
                <button
                  type="button"
                  className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  disabled={idx === 0}
                  onClick={() => setPriority((p) => movePriority(p, idx, -1))}
                >
                  <CaretUp size={14} />
                </button>
                <button
                  type="button"
                  className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  disabled={idx === priority.length - 1}
                  onClick={() => setPriority((p) => movePriority(p, idx, 1))}
                >
                  <CaretDown size={14} />
                </button>
                <button
                  type="button"
                  className="text-[11px] text-red-600 px-1"
                  onClick={() => setPriority((p) => p.filter((x) => x !== net))}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
          {availableForPriority.length > 0 && (
            <select
              className="mt-2 h-8 text-xs border border-slate-200 rounded-md bg-white"
              value=""
              onChange={(e) => {
                const v = e.target.value;
                if (v) setPriority((p) => [...p, v]);
              }}
            >
              <option value="">Add network to priority…</option>
              {availableForPriority.map((n) => (
                <option key={n.value} value={n.value}>{n.label}</option>
              ))}
            </select>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={runSuggest}
            disabled={loading}
            data-testid="suggest-run"
          >
            <MagnifyingGlass size={14} className="mr-1.5" />
            {loading ? "Checking…" : "Check matches"}
          </Button>
          {summary && (
            <span className="text-xs text-slate-500 font-mono">
              {summary.total} found · {summary.routeable} routeable · {summary.no_match} no match
            </span>
          )}
        </div>
      </div>

      {items.length > 0 && (
        <>
          <div className="mb-3 flex items-center gap-3 flex-wrap">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                className="h-8 text-xs pl-8 w-64"
                placeholder="Search results…"
                value={resultSearch}
                onChange={(e) => setResultSearch(e.target.value)}
                data-testid="suggest-result-search"
              />
            </div>
            <select
              className="h-8 text-xs border border-slate-200 rounded-md bg-white px-2"
              value={resultFilter}
              onChange={(e) => setResultFilter(e.target.value)}
            >
              <option value="all">Show all</option>
              <option value="routeable">Routeable only</option>
              <option value="no_match">No match only</option>
            </select>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={() => selectAllRouteable(true)}
              disabled={!routeableItems.length}
            >
              Select all routeable ({routeableItems.length})
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={() => selectAllRouteable(false)}
              disabled={!selected.size}
            >
              Clear selection
            </Button>
            {selectedRouteable.length > 0 && (
              <Button
                size="sm"
                className="h-8 text-xs"
                onClick={() => setConfirmOpen(true)}
                disabled={applying}
                data-testid="suggest-route-selected"
              >
                <ArrowsLeftRight size={14} className="mr-1" />
                Route selected ({selectedRouteable.length})
              </Button>
            )}
            {resultSearch.trim() && (
              <span className="text-[11px] text-slate-400 font-mono">
                showing {visibleItems.length}
              </span>
            )}
          </div>

          <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
            <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="px-3 py-2 w-10">
                      <Checkbox
                        checked={allRouteableSelected}
                        onCheckedChange={(v) => selectAllRouteable(!!v)}
                        aria-label="Select all routeable"
                      />
                    </th>
                    <th className="px-3 py-2">Source</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Domain / geo</th>
                    <th className="px-3 py-2 text-right">Clicks</th>
                    <th className="px-3 py-2">Suggested route</th>
                    <th className="px-3 py-2">Options</th>
                    <th className="px-3 py-2">Tracking URL</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {visibleItems.map((r) => {
                    const canSelect = !!r.route_available && !!r.route_brand_id;
                    const checked = selected.has(r.source_brand_id);
                    return (
                      <tr
                        key={r.source_brand_id}
                        className={checked ? "bg-sky-50/70 hover:bg-sky-50" : "hover:bg-slate-50/80"}
                      >
                        <td className="px-3 py-2 align-top">
                          <Checkbox
                            checked={checked}
                            disabled={!canSelect}
                            onCheckedChange={(v) => toggleRow(r.source_brand_id, !!v)}
                          />
                        </td>
                        <td className="px-3 py-2 align-top">
                          <div className="font-medium text-slate-900">{r.source_brand_name || "—"}</div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <NetworkPill network={r.source_network} />
                            <span className="font-mono text-[10px] text-slate-400">{r.source_brand_id}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 align-top">
                          <StatusPill status={r.source_status} />
                        </td>
                        <td className="px-3 py-2 align-top text-xs text-slate-600">
                          <div>{r.source_domain || "—"}</div>
                          <div className="text-slate-400">{r.source_country || "no geo"}</div>
                        </td>
                        <td className="px-3 py-2 align-top text-right font-mono text-xs">
                          <div>{(r.clicks || 0).toLocaleString()}</div>
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            {(r.valid_clicks ?? 0).toLocaleString()} valid
                            {" · "}
                            {(r.blocked_clicks ?? 0).toLocaleString()} blocked
                          </div>
                        </td>
                        <td className="px-3 py-2 align-top">
                          {r.route_available ? (
                            <>
                              <div className="flex items-center gap-1.5">
                                <span className="text-[10px] text-slate-400 font-bold">#{r.route_priority}</span>
                                <NetworkPill network={r.route_network} />
                              </div>
                              <div className="text-xs font-medium text-slate-800 mt-0.5">
                                {r.route_brand_name}
                              </div>
                              <div className="font-mono text-[10px] text-slate-400">{r.route_brand_id}</div>
                              <div className="text-[10px] text-emerald-700 mt-0.5">active · domain+geo</div>
                            </>
                          ) : (
                            <span className="text-xs text-slate-400">No domain+geo match</span>
                          )}
                        </td>
                        <td className="px-3 py-2 align-top text-[10px] text-slate-500 max-w-[200px]">
                          {(r.route_options || []).map((o) => (
                            <div key={`${o.network}-${o.brand_id}`}>
                              {o.priority}. {o.network}: {o.brand_name}
                            </div>
                          ))}
                          {!(r.route_options || []).length && "—"}
                        </td>
                        <td className="px-3 py-2 align-top">
                          {r.tracking_url ? (
                            <a
                              href={r.tracking_url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-[11px] text-sky-700 hover:underline font-mono break-all"
                            >
                              {r.tracking_url.replace(/^https?:\/\//, "")}
                              <ArrowSquareOut size={11} />
                            </a>
                          ) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!loading && items.length === 0 && summary && (
        <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-md p-8 text-center">
          No brands matched this scope.
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Route {selectedRouteable.length} selected brands?</AlertDialogTitle>
            <AlertDialogDescription>
              Clicks on the PubAdHub (source) tracking URLs will redirect to the suggested
              network. Source offers will <strong>not</strong> be paused. Targets will be
              auto-assigned if missing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={applying}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={applySelected} disabled={applying}>
              {applying ? "Routing…" : "Route selected"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
